import { Box, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import MicOffIcon from '@mui/icons-material/MicOff';

export interface VoiceInputButtonProps {
  isRecording: boolean;
  isSupported: boolean;
  disabled?: boolean;
  onClick: () => void;
  size?: number;
  /** Tooltipy; nadpisuj tylko gdy potrzebujesz innej lokalizacji. */
  tooltipStart?: string;
  tooltipStop?: string;
  tooltipUnsupported?: string;
  /** Jeśli podane — pokaż jako tooltip (np. komunikat błędu mikrofonu). */
  errorText?: string | null;
}

/**
 * Ikonka mikrofonu do wklejenia w composerze czatu.
 *
 *  - stan neutralny: ikona mikrofonu, akcent kolor #6B6BD6 (ten sam co ikona załączników w AIDialog)
 *  - stan nagrywania: ikona „stop", akcent czerwony + delikatne pulsowanie ringa
 *  - brak wsparcia przeglądarki / disabled: szary + tooltip informujący dlaczego
 */
export default function VoiceInputButton({
  isRecording,
  isSupported,
  disabled,
  onClick,
  size = 36,
  tooltipStart = 'Dyktuj głosem (mikrofon)',
  tooltipStop = 'Zatrzymaj dyktowanie',
  tooltipUnsupported = 'Rozpoznawanie mowy nie jest wspierane w tej przeglądarce (użyj Chrome)',
  errorText,
}: VoiceInputButtonProps) {
  const effectivelyDisabled = !isSupported || !!disabled;
  const title = errorText
    ? `Mikrofon: ${errorText}`
    : !isSupported
      ? tooltipUnsupported
      : isRecording
        ? tooltipStop
        : tooltipStart;
  const color = effectivelyDisabled
    ? '#B9B9B9'
    : isRecording
      ? '#E3322A'
      : '#6B6BD6';
  const hoverBg = isRecording ? 'rgba(227,50,42,0.08)' : 'rgba(63,63,220,0.08)';

  return (
    <Tooltip title={title}>
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          if (!effectivelyDisabled) onClick();
        }}
        disabled={effectivelyDisabled}
        aria-label={title}
        aria-pressed={isRecording}
        sx={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          background: 'transparent',
          cursor: effectivelyDisabled ? 'default' : 'pointer',
          color,
          userSelect: 'none',
          alignSelf: 'center',
          p: 0,
          borderRadius: '50%',
          transition: 'background-color 120ms ease, color 120ms ease',
          '&:hover': effectivelyDisabled ? {} : { backgroundColor: hoverBg },
          '&:focus-visible': { outline: '2px solid rgba(63,63,220,0.4)', outlineOffset: 2 },
          ...(isRecording
            ? {
                '@keyframes vd-pulse': {
                  '0%': { boxShadow: '0 0 0 0 rgba(227,50,42,0.55)' },
                  '70%': { boxShadow: '0 0 0 8px rgba(227,50,42,0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(227,50,42,0)' },
                },
                animation: 'vd-pulse 1.4s ease-out infinite',
              }
            : {}),
        }}
      >
        {!isSupported ? (
          <MicOffIcon sx={{ fontSize: Math.round(size * 0.55) }} />
        ) : isRecording ? (
          <StopIcon sx={{ fontSize: Math.round(size * 0.55) }} />
        ) : (
          <MicIcon sx={{ fontSize: Math.round(size * 0.55) }} />
        )}
      </Box>
    </Tooltip>
  );
}
